import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { IFieldValue } from '~src/httpClient/serverApi/types/IFieldValue';
import { FilterFieldNames, ILeafFilterClause } from '~src/spacesPlatform/models/IFilterClause';
import { Users } from '~src/userManagement/services/resources/Users';

import { IContentRelayPlanInfo } from '../models/IContentRelayPlanInfo';
import { ContentRelayPlansResourceQuery, LIST_PLANS_MAX_PAGE_SIZE } from './ContentRelayPlansResourceQuery';

/**
 * Filter panel typeahead values for the Content Relay plan list. getFieldValues doesn't know plans, so
 * the values come from the plans themselves: each owner of a plan matching the other active filters,
 * with that owner's plan count, named via the users API. Only the owner field is supported. Like the
 * list's own filtering, this reads the first `listPlans` page only (see ContentRelayPlansResourceQuery).
 */
export class ContentRelayPlanFieldValuesQuery {

  private filterClauses: ILeafFilterClause[] = [];
  private matchText: string = '';

  constructor(private field: string) { }

  public getSlice(start: number, size: number): Promise<ListResult<IFieldValue>> {
    if (this.field !== FilterFieldNames.ownerId) {
      return Promise.resolve(ListResult.empty<IFieldValue>());
    }
    const matchText = (this.matchText || '').toLowerCase();
    return new ContentRelayPlansResourceQuery().withFilters(this.filterClauses || [])
      .getSlice(0, LIST_PLANS_MAX_PAGE_SIZE)
      .then((plans: ListResult<IContentRelayPlanInfo>) => {
        const counts: { [ownerId: string]: number } = {};
        const ownerIds: string[] = [];
        plans.items.forEach((plan) => {
          // listPlans reads ownerId 0 as "unset".
          if (!(Number(plan.ownerId) > 0)) {
            return;
          }
          if (!counts[plan.ownerId]) {
            counts[plan.ownerId] = 0;
            ownerIds.push(plan.ownerId);
          }
          counts[plan.ownerId]++;
        });
        if (!ownerIds.length) {
          return [];
        }
        return Users.instance.getUsersById(ownerIds).then((users) => {
          const values: IFieldValue[] = [];
          (users.items || []).forEach((user: any) => {
            const id = String(user.id);
            if (counts[id]) {
              values.push({ value: id, label: user.displayName || user.username || '', count: counts[id] });
            }
          });
          return values;
        });
      })
      .then((values: IFieldValue[]) => {
        // Prefix match on any word of the name, like the standard owner typeahead (matchPrefixesOnly).
        const matching = values.filter((value) => {
          return !matchText || value.label.toLowerCase().split(/\s+/).some((word) => word.indexOf(matchText) === 0);
        });
        matching.sort((a, b) => a.label.localeCompare(b.label));
        const from = start || 0;
        return new ListResult<IFieldValue>(matching.slice(from, from + size), from, matching.length, [], from + size < matching.length);
      });
  }

  public getFilters(): ILeafFilterClause[] {
    return this.filterClauses;
  }

  public withMatchText(matchText: string): ContentRelayPlanFieldValuesQuery {
    const query = this.clone();
    query.matchText = matchText;
    return query;
  }

  public withFilters(filterClauses: ILeafFilterClause[]): ContentRelayPlanFieldValuesQuery {
    const query = this.clone();
    query.filterClauses = filterClauses;
    return query;
  }

  private clone(): ContentRelayPlanFieldValuesQuery {
    const query = new ContentRelayPlanFieldValuesQuery(this.field);
    query.filterClauses = this.filterClauses;
    query.matchText = this.matchText;
    return query;
  }
}
